/* ============================ OPERATIONS PAGES ============================
   monitoring, alerts, jobs, job, catalog, images, playbooks, rollouts, backups, recycle
   ========================================================================== */
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { DB, act, api, can, fire, go, job, node, rerender, site, ui, wl } from "../lib/store";
import { archFits, archOf } from "../lib/arch";
import { ago, gb, inFuture, kbSize, since, when } from "../lib/fmt";
import { onJobLog } from "../lib/live";
import { Badge, Chips, Meter } from "../ui/common";
import { Modal } from "../ui/modal";
import { PAGES, type PageProps } from "./registry";
import { MonitoringPanel } from "./workload";
import {
  addImage, alertRule, deleteItem, installApp, publishApp, replaceDevice, repo, restore, rollout, runPlaybook, schedule,
} from "../modals/ops";
import { addBackup, pushImage } from "../modals/account";

/* ---------- monitoring ---------- */
/* one row per thing that has charts: every edge device and every managed VM / LXC */
type MonTarget = { id: string; kind: "device" | "vm" | "lxc"; name: string; device: any; site: any; status: string;
  cpu: number; ram: number; disk: number; tags: string[]; ip: string; alerts: number };
const HOT = 85;
const monF = (): any => (ui.sub.mon ||= { sort: "name" });
const STATUS_GROUPS: Record<string, string[]> = {
  up: ["online", "running"], down: ["offline", "stopped", "exited"], attention: ["degraded", "unknown", "stale", "provisioning"],
};
function monTargets(): MonTarget[] {
  const openAlerts = (name: string) => DB.alerts.filter((a: any) => !a.resolved && !a.ack && String(a.target || "").includes(name)).length;
  return [
    ...DB.nodes.map((n: any) => ({ id: "n" + n.id, kind: "device" as const, name: n.name, device: n, site: site(n.site), status: n.status,
      cpu: n.cpu || 0, ram: n.ram || 0, disk: n.disk || 0, tags: [], ip: n.ip || n.lan || "", alerts: openAlerts(n.name) })),
    ...DB.workloads.filter((w: any) => !w.external).map((w: any) => {
      const n = node(w.node);
      return { id: "w" + w.id, kind: w.type, name: w.name, device: n, site: site(n?.site), status: w.status,
        cpu: w.cpu || 0, ram: w.ram || 0, disk: w.disk || 0, tags: w.tags || [], ip: w.ip || "", alerts: openAlerts(w.name) };
    }),
  ];
}
function MonitoringPage({ param }: PageProps) {
  const f = monF();
  const setF = (k: string, v: string) => { f[k] = v; rerender(); };
  const all = monTargets();
  if (!all.length) return <div className="empty"><h3>Nothing to monitor yet</h3><p>Add an edge device first.</p></div>;
  const q = String(f.q || "").trim().toLowerCase();
  const list = all.filter((t) => {
    if (q && ![t.name, t.ip, t.device?.name, ...t.tags].some((x) => String(x || "").toLowerCase().includes(q))) return false;
    if (f.kind && t.kind !== f.kind) return false;
    if (f.device && String(t.device?.id) !== f.device) return false;   // the box itself and everything on it
    if (f.site && String(t.site?.id) !== f.site) return false;
    if (f.tag && !t.tags.includes(f.tag)) return false;
    if (f.status && !(STATUS_GROUPS[f.status] || []).includes(t.status)) return false;
    if (f.hot && !(t.cpu >= HOT || t.ram >= HOT || t.disk >= HOT || t.alerts)) return false;
    return true;
  });
  const key = f.sort || "name";
  list.sort((a, b) => key === "name" ? a.name.localeCompare(b.name)
    : key === "device" ? String(a.device?.name).localeCompare(String(b.device?.name)) || a.name.localeCompare(b.name)
    : (b as any)[key] - (a as any)[key]);
  const tags = [...new Set(DB.workloads.flatMap((w: any) => w.tags || []))].sort() as string[];
  const sel: string = param && list.some((t) => t.id === param) ? param : list[0]?.id;
  const cur = list.find((t) => t.id === sel);
  const isNode = sel?.[0] === "n", id = sel?.slice(1);
  const avg = (k: "cpu" | "ram" | "disk") => (list.length ? Math.round(list.reduce((s, t) => s + t[k], 0) / list.length) : 0);
  const hot = list.filter((t) => t.cpu >= HOT || t.ram >= HOT || t.disk >= HOT).length;
  const any = Object.entries(f).some(([k, v]) => k !== "sort" && v);
  return (
    <>
      <div className="pagehead">
        <div><h2>Monitoring</h2>
          <Chips items={[[list.length, list.length === all.length ? "monitored" : `of ${all.length} shown`], [avg("cpu") + "%", "avg CPU"], [avg("ram") + "%", "avg memory"],
            [avg("disk") + "%", "avg disk"], hot ? [hot, `at ${HOT}%+`, "bad"] : false]} />
          <p>History comes straight from each device's Proxmox RRD, so charts keep filling even while this console is unreachable. Pick a row to see its charts.</p></div>
        <div className="spacer" />
        <div className="btnrow"><button className="btn" onClick={() => go("alerts")}>Alert rules</button></div>
      </div>
      <div className="tbl-wrap" style={{ borderBottom: 0, borderRadius: "8px 8px 0 0" }}>
        <div className="toolbar">
          <input type="search" placeholder="Search name, IP, tag…" value={f.q || ""} onChange={(e) => setF("q", e.target.value)} style={{ minWidth: 180 }} />
          <select value={f.device || ""} onChange={(e) => setF("device", e.target.value)}><option value="">All edge boxes</option>
            {DB.nodes.map((n: any) => <option key={n.id} value={String(n.id)}>{n.name}</option>)}</select>
          <select value={f.site || ""} onChange={(e) => setF("site", e.target.value)}><option value="">All sites</option>
            {DB.sites.map((s: any) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}</select>
          <select value={f.kind || ""} onChange={(e) => setF("kind", e.target.value)}><option value="">Devices, VMs and LXC</option>
            <option value="device">Edge devices only</option><option value="vm">VMs only</option><option value="lxc">LXC only</option></select>
          <select value={f.tag || ""} onChange={(e) => setF("tag", e.target.value)} disabled={!tags.length}><option value="">{tags.length ? "Any tag" : "No tags yet"}</option>
            {tags.map((t) => <option key={t} value={t}>{t}</option>)}</select>
          <select value={f.status || ""} onChange={(e) => setF("status", e.target.value)}><option value="">Any status</option>
            <option value="up">Online / running</option><option value="attention">Degraded / unknown</option><option value="down">Offline / stopped</option></select>
          <label className="muted" style={{ display: "inline-flex", gap: 5, alignItems: "center", fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={!!f.hot} onChange={(e) => { f.hot = e.target.checked; rerender(); }} /> Needs attention ({HOT}%+ or open alert)</label>
          {any ? <button className="btn sm ghost" onClick={() => { ui.sub.mon = { sort: f.sort }; rerender(); }}>Clear</button> : null}
          <div style={{ flex: 1 }} />
          <select value={key} onChange={(e) => setF("sort", e.target.value)} title="Sort">
            <option value="name">Sort: name</option><option value="device">Sort: edge box</option><option value="cpu">Sort: CPU ↓</option>
            <option value="ram">Sort: memory ↓</option><option value="disk">Sort: disk ↓</option><option value="alerts">Sort: open alerts ↓</option></select>
        </div></div>
      <div className="tbl-wrap" style={{ borderRadius: "0 0 8px 8px", marginBottom: 18, maxHeight: 420, overflow: "auto" }}>
        <table><thead><tr><th>Name</th><th>Type</th><th>Edge box</th><th>Status</th><th style={{ width: 150 }}>CPU</th><th style={{ width: 150 }}>Memory</th><th style={{ width: 150 }}>Disk</th><th>Tags</th></tr></thead><tbody>
          {list.length ? list.map((t) => (
            <tr key={t.id} className="row" style={{ cursor: "pointer" }} onClick={() => go("monitoring", t.id)}>
              <td style={t.id === sel ? { boxShadow: "inset 3px 0 0 var(--accent)", background: "var(--accent-soft)" } : undefined}>
                <b style={{ fontWeight: 500 }}>{t.name}</b>{t.alerts ? <span className="badge bad" style={{ marginLeft: 6 }}>{t.alerts} alert{t.alerts > 1 ? "s" : ""}</span> : null}
                {t.ip ? <div className="muted mono" style={{ fontSize: 11.5 }}>{t.ip}</div> : null}</td>
              <td>{t.kind === "device" ? "Edge device" : t.kind.toUpperCase()}</td>
              <td>{t.kind === "device" ? <span className="muted">{t.site?.name}</span> : <>{t.device?.name || "—"}<div className="muted" style={{ fontSize: 11.5 }}>{t.site?.name}</div></>}</td>
              <td><Badge s={t.status} /></td>
              <td><Meter v={t.cpu} /></td><td><Meter v={t.ram} /></td><td><Meter v={t.disk} /></td>
              <td>{t.tags.map((g) => <span key={g} className="badge" style={{ marginRight: 4, cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); setF("tag", g); }} title="Filter by this tag">{g}</span>)}</td>
            </tr>
          )) : <tr><td colSpan={8} className="muted">Nothing matches these filters.</td></tr>}
        </tbody></table></div>
      {cur ? <MonitoringPanel key={sel} kind={isNode ? "node" : "workload"} id={id} name={cur.name + (cur.kind === "device" ? "" : ` · ${cur.device?.name || ""}`)}
        cpu={cur.cpu} ram={cur.ram} disk={cur.disk}
        ctrs={isNode ? undefined : DB.containers.filter((c: any) => String(c.workload) === String(id))} /> : null}
    </>
  );
}
PAGES.monitoring = { crumb: () => "Monitoring", C: MonitoringPage };

/* ---------- alerts ---------- */
const sevCls = (s: string) => (s === "critical" ? "bad" : s === "warning" ? "warn" : "acc");
const metricName = (m: string) => (({
  memory: "Memory usage", cpu: "CPU usage", disk: "Disk usage", restarts: "Container restarts", checkin: "Agent check-in",
  backup: "Backup result", cert: "Certificate expiry",
}) as Record<string, string>)[m] || m;

function AlertTarget({ a }: { a: any }) {
  if (a.link && a.link.startsWith("node:"))
    return <a style={{ cursor: "pointer" }} onClick={() => go("node", a.link.split(":")[1])}>{a.target}</a>;
  if (a.link && a.link.startsWith("workload:"))
    return <a style={{ cursor: "pointer" }} onClick={() => go("workload", a.link.split(":")[1])}>{a.target}</a>;
  return <>{a.target}</>;
}

function AlertsPage() {
  const open = DB.alerts.filter((a: any) => !a.resolved), resolved = DB.alerts.filter((a: any) => a.resolved);
  return (
    <>
      <div className="pagehead">
        <div><h2>Alerts</h2><p>What is wrong now, and the rules that decide what counts as wrong. Rules are checked every {DB.settings.connectivity?.checkin_s || 20}s; alerts close on their own when the condition clears.</p></div>
        <div className="spacer" />
        {can("Admin") ? <button className="btn primary" onClick={() => alertRule()}>New alert rule</button> : null}
      </div>
      <div className="tbl-wrap"><table><thead><tr><th>Severity</th><th>Rule</th><th>Target</th><th>Open for</th><th></th></tr></thead><tbody>
        {open.length ? open.map((a: any) => (
          <tr key={"o" + a.id} className={"row " + (a.ack || a.silenced_until ? "dim" : "")}>
            <td><span className={"badge " + sevCls(a.sev)}><span className="dot" />{a.sev}</span></td>
            <td><b style={{ fontWeight: 500 }}>{a.rule}</b>{a.silenced_until ? <div className="muted" style={{ fontSize: 12 }}>silenced until {when(a.silenced_until)}</div> : null}</td>
            <td><AlertTarget a={a} /></td><td className="mono">{since(a.since)}</td>
            <td style={{ textAlign: "right" }}>{can("Operator") ? (
              <div className="btnrow" style={{ justifyContent: "flex-end" }}>
                <button className="btn sm" onClick={() => fire(act("POST", `/api/alerts/${a.id}/ack`, undefined, a.ack ? "Acknowledgement removed" : "Alert acknowledged", a.rule))}>{a.ack ? "Acknowledged" : "Acknowledge"}</button>
                <button className="btn sm ghost" onClick={() => fire(act("POST", `/api/alerts/${a.id}/silence`, { hours: 2 }, "Silenced for 2 hours", a.rule))}>Silence</button>
              </div>) : null}</td>
          </tr>
        )) : (
          <tr><td colSpan={5}><div className="empty" style={{ padding: 26 }}><h3>No open alerts</h3><p>Everything is inside its thresholds.</p></div></td></tr>
        )}
        {resolved.slice(0, 15).map((a: any) => (
          <tr key={"r" + a.id} className="row dim"><td><span className="badge ok"><span className="dot" />resolved</span></td><td>{a.rule}</td><td>{a.target}</td><td className="mono">closed {ago(a.resolved)}</td><td></td></tr>
        ))}
      </tbody></table></div>
      <div className="section-title">Alert rules</div>
      <div className="tbl-wrap"><table><thead><tr><th>Rule</th><th>Watches</th><th>Scope</th><th>Severity</th><th>Notifies</th><th>Enabled</th><th></th></tr></thead><tbody>
        {DB.rules.map((r: any) => (
          <tr key={r.id} className="row"><td><b style={{ fontWeight: 500 }}>{r.name}</b></td><td>{metricName(r.metric)} {r.op} {r.value} · {r.for_min} min</td><td>{r.scope}</td>
            <td><span className={"badge " + sevCls(r.sev)}>{r.sev}</span></td><td>{r.channels}</td>
            <td><input type="checkbox" checked={!!r.on} disabled={!can("Admin")}
              onChange={(e) => { const on = e.target.checked; fire(act("PUT", `/api/rules/${r.id}`, { on }, "Rule " + (on ? "enabled" : "disabled"), r.name)); }} /></td>
            <td style={{ textAlign: "right" }}>{can("Admin") ? (
              <button className="btn sm ghost" onClick={() => Modal.confirm(`Delete rule “${r.name}”?`, "Open alerts from this rule are closed too.", "Delete",
                () => fire(act("DELETE", `/api/rules/${r.id}`, undefined, "Rule deleted")))}>Delete</button>) : null}</td>
          </tr>
        ))}
      </tbody></table></div>
    </>
  );
}
PAGES.alerts = { crumb: () => "Alerts", C: AlertsPage };

/* ---------- jobs ---------- */
export function JobTable({ list }: { list: any[] }) {
  if (!list.length) return <div className="empty"><h3>No jobs yet</h3><p>Anything you deploy or change from this console shows up here with its full log.</p></div>;
  return (
    <div className="tbl-wrap"><table><thead><tr><th>Job</th><th>Action</th><th>Target</th><th>Started by</th><th>Started</th><th>Duration</th><th>Status</th><th></th></tr></thead><tbody>
      {list.map((j: any) => (
        <tr key={j.id} className="row"><td className="mono"><a onClick={() => go("job", j.id)} style={{ cursor: "pointer" }}>{j.id}</a></td>
          <td><b style={{ fontWeight: 500 }}>{j.action}</b>{j.note ? <div style={{ fontSize: 12, color: "var(--warn)" }}>{j.note}</div> : null}
            {j.status === "failed" && j.err ? <div style={{ fontSize: 12, color: "var(--bad)" }}>{String(j.err).slice(0, 140)}</div> : null}</td>
          <td>{j.target}</td><td>{j.by}</td><td>{j.startedTxt}</td><td className="mono">{j.dur}</td>
          <td><Badge s={j.status} /></td>
          <td style={{ textAlign: "right" }}><div className="btnrow" style={{ justifyContent: "flex-end" }}>
            {j.status === "failed" && can("Operator") ? <button className="btn sm" onClick={() => fire(job("POST", `/api/jobs/${j.id}/retry`))}>Retry</button> : null}
            {(j.status === "running" || j.status === "queued") && can("Operator")
              ? <button className="btn sm" onClick={() => fire(act("POST", `/api/jobs/${j.id}/cancel`, undefined, "Job cancelled", j.id))}>Cancel</button> : null}
            <button className="btn sm ghost" onClick={() => go("job", j.id)}>Log</button></div></td>
        </tr>
      ))}
    </tbody></table></div>
  );
}

function JobsPage() {
  return (
    <>
      <div className="pagehead">
        <div><h2>Tasks and jobs</h2><p>Every change runs as a job with a full log: Proxmox API tasks, SSH steps and Ansible playbooks. Offline devices keep their jobs queued until they come back.</p></div>
        <div className="spacer" />
        <div className="btnrow">{can("Operator") ? <>
          <button className="btn" onClick={() => schedule()}>Schedule a job</button>
          <button className="btn primary" onClick={() => runPlaybook()}>Run playbook</button></> : null}</div>
      </div>
      <JobTable list={DB.jobs} />
      {DB.schedules.length ? <>
        <div className="section-title">Scheduled</div>
        <div className="tbl-wrap"><table><thead><tr><th>Playbook</th><th>Against</th><th>When</th><th>Next run</th><th>Last run</th><th>Window</th><th>On</th><th></th></tr></thead><tbody>
          {DB.schedules.map((s: any) => (
            <tr key={s.id} className="row"><td className="mono">{s.playbook}</td><td>{s.scope}</td><td>{s.label} <span className="muted mono">({s.cron})</span></td>
              <td>{inFuture(s.next)}</td><td>{s.last_run ? when(s.last_run) : "—"}</td><td>{s.window ? "yes" : "no"}</td>
              <td><input type="checkbox" checked={!!s.on}
                onChange={(e) => { const on = e.target.checked; fire(act("PUT", `/api/schedules/${s.id}`, { on }, "Schedule " + (on ? "enabled" : "paused"))); }} /></td>
              <td style={{ textAlign: "right" }}>{can("Admin") ? <button className="btn sm ghost" onClick={() => fire(act("DELETE", `/api/schedules/${s.id}`, undefined, "Schedule removed"))}>Remove</button> : null}</td>
            </tr>
          ))}
        </tbody></table></div>
      </> : null}
    </>
  );
}
PAGES.jobs = { crumb: () => "Tasks & jobs", C: JobsPage };

/* ---------- a single job with its streamed log ---------- */
const errHint = (e: string) => {
  if (/manifest unknown|not found: manifest|pull access denied/i.test(e)) return "An image tag does not exist in its registry. Push the image or point the service at a tag that exists, then retry.";
  if (/snapshot feature is not available/i.test(e)) return "This guest's disk is on storage that cannot snapshot (raw files on a directory storage). Use “Back up now” instead, or move the disk to LVM-thin / ZFS / qcow2.";
  if (/CT is locked|VM is locked/i.test(e)) return "Proxmox has the guest locked by another task (often a running backup). Retry when it finishes.";
  if (/No space left/i.test(e)) return "The device ran out of disk space. Free space or grow the storage, then retry.";
  if (/ssh .* failed|Permission denied|no usable SSH user/i.test(e)) return "The console could not log in over SSH. Use “Enable management” on the workload, or re-import it with credentials.";
  if (/timed out|timeout/i.test(e)) return "A step took too long. The device may be slow or offline; open the log for the last step that ran.";
  return "The last lines of the log show which step failed.";
};

function JobPage({ param }: PageProps) {
  const id = param || "";
  const j: any = DB.jobs.find((x: any) => x.id === id) || { id, action: "Loading…", target: "", status: "", by: "", startedTxt: "", dur: "" };
  const [lines, setLines] = useState<[string | null, string][] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const pre = useRef<HTMLPreElement>(null);
  const stick = useRef(false);
  useEffect(() => {
    let alive = true;
    setLines(null); setErr(null);
    api("GET", "/api/jobs/" + id).then(
      (d: any) => { if (!alive) return; stick.current = true; setLines(d.log || []); },
      (e: any) => { if (alive) setErr(e.message); },
    );
    const un = onJobLog((jid, ls) => {
      if (jid !== id) return;
      const p = pre.current;
      // keep following the log only when the reader is already at the bottom
      stick.current = !!p && p.scrollTop + p.clientHeight >= p.scrollHeight - 30;
      setLines((prev) => (prev ? [...prev, ...ls] : prev));
    });
    return () => { alive = false; un(); };
  }, [id]);
  useLayoutEffect(() => {
    if (stick.current && pre.current) pre.current.scrollTop = pre.current.scrollHeight;
    stick.current = false;
  }, [lines]);
  return (
    <>
      <div className="pagehead">
        <div>
          <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}><a onClick={() => go("jobs")} style={{ cursor: "pointer" }}>Tasks</a> / {id}</div>
          <h2>{j.action} {j.status ? <Badge s={j.status} /> : null}</h2>
          <p>{j.target} · started by {j.by} · {j.startedTxt} · {j.dur}
            {j.queue ? <> · queue <span className="mono">{j.queue}</span></> : null}
            {j.worker ? <> · worker <span className="mono">{j.worker}</span></> : null}</p>
        </div>
        <div className="spacer" />
        <div className="btnrow">
          {j.status === "failed" && can("Operator") ? <button className="btn primary" onClick={() => fire(job("POST", `/api/jobs/${id}/retry`))}>Retry</button> : null}
          {(j.status === "running" || j.status === "queued") && can("Operator")
            ? <button className="btn" onClick={() => fire(act("POST", `/api/jobs/${id}/cancel`, undefined, "Job cancelled", id))}>Cancel job</button> : null}
          <a className="btn" href={`/api/jobs/${id}/log.txt`}>Download log</a>
        </div>
      </div>
      {j.note ? <div className="banner acc"><span>◈</span><div><b>{j.note}</b></div></div> : null}
      {j.status === "failed" && j.err ? <div className="banner"><span>△</span><div><b>{String(j.err).slice(0, 400)}</b>{errHint(String(j.err))}</div></div> : null}
      <div className="card">
        <div className="hd"><h3>Output</h3><div className="spacer" />{j.status === "running" ? <span className="spin" /> : null}
          <span className="badge" id="jobcount-lines">{lines ? lines.length + " lines" : "…"}</span></div>
        <div className="bd"><pre className="log" id="joblog" ref={pre} style={{ maxHeight: 560 }}>
          {err != null ? err : lines ? lines.map(([c, t], i) => (
            <Fragment key={i}>{i ? "\n" : ""}<span className={c || undefined}>{t}</span></Fragment>
          )) : "loading…"}
        </pre></div>
      </div>
    </>
  );
}
PAGES.job = { crumb: (param) => `Job ${param ?? ""}`, C: JobPage };

/* ---------- catalog ---------- */
function CatalogPage() {
  return (
    <>
      <div className="pagehead">
        <div><h2>App catalog</h2><p>Tested stacks with pinned versions and memory limits. Pick a target machine and the console writes the compose file for you.</p></div>
        <div className="spacer" />
        {can("Admin") ? <button className="btn" onClick={() => publishApp()}>Publish a private app</button> : null}
      </div>
      <div className="catalog">{DB.catalog.map((a: any) => (
        <div key={a.id} className="app">
          <div className="glyph">{a.icon}</div><b>{a.name}</b><p>{a.desc}</p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <span className={"badge " + (a.private ? "acc" : "")}>{a.tag}</span><span className="badge">{a.ram} RAM</span>
            {a.port ? <span className="badge">:{a.port}</span> : null}</div>
          <div className="btnrow" style={{ marginTop: 6 }}>
            {can("Operator") ? <button className="btn primary sm" onClick={() => installApp(a.id)}>Install</button> : null}
            {a.private && can("Admin") ? <button className="btn sm ghost" onClick={() => fire(act("DELETE", `/api/catalog/${a.id}`, undefined, "App removed from the catalog"))}>Remove</button> : null}
          </div>
        </div>
      ))}</div>
    </>
  );
}
PAGES.catalog = { crumb: () => "App catalog", C: CatalogPage };

/* ---------- images ---------- */
/* filters survive re-renders and navigation, separately from the Workloads filters */
const imgF = (): any => (ui.sub.img ||= {});
function ImagesPage() {
  const ad = can("Admin");
  const f = imgF();
  const setF = (k: string, v: string) => { f[k] = v; rerender(); };
  const fitNode: any = f.fits ? node(f.fits) : null;
  const q = String(f.q || "").trim().toLowerCase();
  const list = DB.images.filter((i: any) => {
    const a = archOf(i.arch), on = (i.cached_on || []).map(String);
    if (q && !`${i.name} ${i.filename || ""}`.toLowerCase().includes(q)) return false;
    if (f.kind && i.kind !== f.kind) return false;
    if (f.arch === "unknown" ? a : f.arch && a !== f.arch) return false;
    if (f.on === "none" ? on.length : f.on && !on.includes(f.on)) return false;
    if (f.appr && (f.appr === "yes") !== !!i.approved) return false;
    if (fitNode && !archFits(i.arch, fitNode.arch)) return false;
    return true;
  });
  const count = (a: string) => DB.images.filter((i: any) => archOf(i.arch) === a).length;
  const kinds = [...new Set(DB.images.map((i: any) => i.kind))].sort() as string[];
  const any = Object.values(f).some(Boolean);
  return (
    <>
      <div className="pagehead">
        <div><h2>Images and templates</h2>
          <Chips items={[[DB.images.length, "images"], [DB.images.filter((i: any) => i.approved).length, "approved", "ok"],
            [count("arm64"), "arm64"], [count("amd64"), "amd64"], [DB.node_total, "devices"]]} />
          <p>Push images to devices ahead of time so a deployment does not wait on a slow uplink. Anything already on a device's storage shows up here on its own.
            New VMs and containers are only offered images that match the device's CPU (arm64 or amd64).</p></div>
        <div className="spacer" />
        {ad ? <button className="btn primary" onClick={() => addImage()}>Add image</button> : null}
      </div>
      <div className="tbl-wrap" style={{ borderBottom: 0, borderRadius: "8px 8px 0 0" }}>
        <div className="toolbar">
          <input type="search" placeholder="Search name or file…" value={f.q || ""} onChange={(e) => setF("q", e.target.value)} style={{ minWidth: 200 }} />
          <select value={f.kind || ""} onChange={(e) => setF("kind", e.target.value)}><option value="">All kinds</option>
            {kinds.map((k) => <option key={k} value={k}>{k}</option>)}</select>
          <select value={f.arch || ""} onChange={(e) => setF("arch", e.target.value)}><option value="">Any architecture</option>
            <option value="arm64">arm64 (ARM / aarch64)</option><option value="amd64">amd64 (x86_64)</option><option value="unknown">Unknown</option></select>
          <select value={f.fits || ""} onChange={(e) => setF("fits", e.target.value)} title="Only images that can run on this device">
            <option value="">Runs on any device</option>
            {DB.nodes.map((n: any) => <option key={n.id} value={String(n.id)}>Runs on {n.name} ({archOf(n.arch) || n.arch || "?"})</option>)}</select>
          <select value={f.on || ""} onChange={(e) => setF("on", e.target.value)}><option value="">Cached anywhere or not</option>
            {DB.nodes.map((n: any) => <option key={n.id} value={String(n.id)}>Cached on {n.name}</option>)}
            <option value="none">Not cached on any device</option></select>
          <select value={f.appr || ""} onChange={(e) => setF("appr", e.target.value)}><option value="">Approved or not</option>
            <option value="yes">Approved</option><option value="no">Review needed</option></select>
          {any ? <button className="btn sm ghost" onClick={() => { ui.sub.img = {}; rerender(); }}>Clear</button> : null}
          <div style={{ flex: 1 }} /><span className="muted" style={{ fontSize: 12.5 }}>{list.length} of {DB.images.length}</span>
        </div></div>
      <div className="tbl-wrap" style={{ borderRadius: "0 0 8px 8px" }}><table><thead><tr><th>Name</th><th>Kind</th><th>Arch</th><th>Size</th><th>Cached on devices</th><th>Approved</th><th></th></tr></thead><tbody>
        {list.length ? list.map((i: any) => {
          const pushable = DB.nodes.some((n: any) => !(i.cached_on || []).map(String).includes(String(n.id)) && archFits(i.arch, n.arch));
          const st = i.approved
            ? <span className="badge ok"><span className="dot" />approved</span>
            : <span className="badge warn"><span className="dot" />review needed</span>;
          return (
            <tr key={i.id} className="row">
              <td><b style={{ fontWeight: 500 }}>{i.name}</b>
                {i.filename && i.filename !== i.name && !i.filename.startsWith("template:") ? <div className="muted mono" style={{ fontSize: 11.5 }}>{i.filename}</div> : null}</td>
              <td>{i.kind}</td><td>{i.arch ? <span className="badge">{archOf(i.arch)}</span> : <span className="muted" title="Not tagged; can be tried on any device">—</span>}</td><td className="mono">{kbSize(i.size)}</td>
              <td title={(i.cached_on || []).map((id: any) => node(id)?.name).filter(Boolean).join(", ")}>{i.cached_n} of {DB.node_total}</td>
              <td>{ad ? <a style={{ cursor: "pointer" }} title="Click to toggle"
                onClick={() => fire(act("PUT", `/api/images/${i.id}`, { approved: !i.approved }, i.approved ? "Approval removed" : "Image approved", i.name))}>{st}</a> : st}</td>
              <td style={{ textAlign: "right" }}><div className="btnrow" style={{ justifyContent: "flex-end" }}>
                {can("Operator") && i.url && pushable ? <button className="btn sm" onClick={() => pushImage(i.id)}>Push to devices…</button> : null}
                {ad ? <button className="btn sm ghost" onClick={() => deleteItem("image", i.id)}>Delete</button> : null}</div></td>
            </tr>
          );
        }) : <tr><td colSpan={7} className="muted">{DB.images.length ? "No image matches these filters." : "No images yet."}</td></tr>}
      </tbody></table></div>
    </>
  );
}
PAGES.images = { crumb: () => "Images & templates", C: ImagesPage };

/* ---------- playbooks ---------- */
function PlaybooksPage() {
  const rp = DB.settings.playbooks || {};
  return (
    <>
      <div className="pagehead">
        <div><h2>Playbooks</h2><p>{rp.repo_url
          ? <>Built-in playbooks plus <code>{rp.repo_url}@{rp.branch || "main"}</code>.</>
          : "Built-in playbooks. Connect a git repository to add your own."} Each run is a job with a full Ansible log.</p></div>
        <div className="spacer" />
        <div className="btnrow">
          {can("Admin") ? <>
            <button className="btn" onClick={() => (rp.repo_url ? fire(job("POST", "/api/playbooks/sync")) : repo())}>Sync repository</button>
            {rp.repo_url ? <button className="btn ghost" onClick={() => repo()}>Change repo</button> : null}
          </> : null}
          {can("Operator") ? <button className="btn primary" onClick={() => runPlaybook()}>Run playbook</button> : null}
        </div>
      </div>
      <div className="tbl-wrap"><table><thead><tr><th>Playbook</th><th>What it does</th><th>Runs on</th><th>Source</th><th>Runs</th><th></th></tr></thead><tbody>
        {DB.playbooks.map((p: any) => (
          <tr key={p.name} className="row"><td className="mono">{p.name}</td><td>{p.desc}</td>
            <td>{(({ any: "devices + guests", guests: "guests", nodes: "devices" }) as Record<string, string>)[p.targets] || p.targets}</td>
            <td className="mono" style={{ fontSize: 12 }}>{p.repo}</td><td>{p.runs}</td>
            <td style={{ textAlign: "right" }}>{can("Operator") ? (
              <div className="btnrow" style={{ justifyContent: "flex-end" }}>
                <button className="btn sm" onClick={() => runPlaybook(undefined, `${p.name}`, true)}>Dry run</button>
                <button className="btn sm primary" onClick={() => runPlaybook(undefined, `${p.name}`)}>Run</button>
              </div>) : null}</td>
          </tr>
        ))}
      </tbody></table></div>
    </>
  );
}
PAGES.playbooks = { crumb: () => "Playbooks", C: PlaybooksPage };

/* ---------- rollouts ---------- */
function RolloutCard({ r }: { r: any }) {
  const cur = r.waves[r.current];
  const stLabel = (({ running: "running", waiting: "waiting for approval", paused: "paused", done: "completed", failed: "failed", rolledback: "rolled back" }) as Record<string, string>)[r.status] || r.status;
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="hd"><h3>{r.name}</h3><div className="spacer" /><span className="muted" style={{ fontSize: 12 }}>{r.by} · {when(r.created)}</span><Badge s={r.status} txt={stLabel} /></div>
      <div className="bd">
        <div style={{ display: "flex", gap: 0, marginBottom: 16, flexWrap: "wrap" }}>
          {r.waves.map((w: any, i: number) => {
            const waiting = i === r.current && r.status === "waiting";
            const cls = w.status === "done" ? "done" : w.status === "running" ? "running" : w.status === "failed" ? "failed" : waiting ? "waiting" : "";
            const txt = w.status === "done" ? "completed" : w.status === "running" ? "running" : w.status === "failed" ? "failed" : waiting ? "waiting for approval" : "not started";
            return (
              <div key={i} className={"wave " + cls}>
                <div style={{ fontSize: 12, color: "var(--ink-2)" }}>{txt}</div>
                <b style={{ fontWeight: 500 }}>{w.label}</b><div className="muted" style={{ fontSize: 11.5 }}>{(w.names || []).join(", ")}</div>
                {w.job_id ? <a style={{ fontSize: 12, cursor: "pointer" }} onClick={() => go("job", "j" + w.job_id)}>j{w.job_id}</a> : null}
              </div>
            );
          })}
        </div>
        <p style={{ color: "var(--ink-2)", marginTop: 0 }}>{r.gate ? "Health gate: every container healthy for 60 s after each wave." : "No health gate."} {r.auto_rollback ? "Rolls back automatically on failure." : ""} {r.window ? "Runs only inside the maintenance window." : ""} {r.note}</p>
        {can("Operator") && !["done", "rolledback"].includes(r.status) ? (
          <div className="btnrow">
            {["waiting", "paused"].includes(r.status) && cur
              ? <button className="btn primary" onClick={() => fire(act("POST", `/api/rollouts/${r.id}/continue`, undefined, "Rollout continuing", cur.label))}>Continue to {cur.label}</button> : null}
            <button className="btn" onClick={() => Modal.confirm(`Roll back ${r.name}?`, "Every target that already received the change goes back to its previous version.", "Roll back",
              () => fire(act("POST", `/api/rollouts/${r.id}/rollback`, undefined, "Rollback started")))}>Roll back everything</button>
            {["running", "waiting"].includes(r.status)
              ? <button className="btn ghost" onClick={() => fire(act("POST", `/api/rollouts/${r.id}/pause`, undefined, "Rollout paused", "No further waves will start"))}>Pause</button> : null}
          </div>) : null}
      </div>
    </div>
  );
}
function RolloutsPage() {
  return (
    <>
      <div className="pagehead">
        <div><h2>Fleet rollouts</h2><p>Ship a change to one site, watch it, then let it spread. If a wave fails its health gate the rollout stops and rolls back on its own.</p></div>
        <div className="spacer" />
        {can("Operator") ? <button className="btn primary" onClick={() => rollout(undefined as any)}>Start a rollout</button> : null}
      </div>
      {DB.rollouts.length ? DB.rollouts.map((r: any) => <RolloutCard key={r.id} r={r} />)
        : <div className="empty"><h3>No rollouts yet</h3><p>Roll a compose stack update, an OS patch or an agent upgrade across sites in waves.</p></div>}
    </>
  );
}
PAGES.rollouts = { crumb: () => "Fleet rollouts", C: RolloutsPage };

/* ---------- backups ---------- */
function BackupsPage() {
  return (
    <>
      <div className="pagehead">
        <div><h2>Backups and restore</h2><p>vzdump backups go to the device's own storage or a shared one (NFS, PBS), so a dead device can be replaced with its workloads intact.</p></div>
        <div className="spacer" />
        <div className="btnrow">
          {can("Admin") ? <button className="btn" onClick={() => replaceDevice()}>Replace a failed device</button> : null}
          {can("Operator") ? <button className="btn primary" onClick={() => addBackup()}>New schedule</button> : null}
        </div>
      </div>
      <div className="tbl-wrap"><table><thead><tr><th>Target</th><th>Schedule</th><th>Last backup</th><th>Size</th><th>Destination</th><th>Restore tested</th><th></th></tr></thead><tbody>
        {DB.backups.length ? DB.backups.map((b: any) => (
          <tr key={b.id} className="row">
            <td><b style={{ fontWeight: 500 }}>{b.workload
              ? <a style={{ cursor: "pointer" }} onClick={() => go("workload", b.workload, "backups")}>{b.target}</a> : b.target}</b>
              <div className="muted" style={{ fontSize: 12 }}>next {inFuture(b.next)}</div></td>
            <td>{b.schedule}</td>
            <td>{b.last ? when(b.last) : "never"}{" "}
              {b.status === "stale" || b.status === "never" ? <span className="badge warn">{b.status}</span> : b.status === "failed" ? <span className="badge bad">failed</span> : null}</td>
            <td className="mono">{gb(b.size)}</td><td>{b.dest}</td>
            <td>{b.verified ? <span className="badge ok"><span className="dot" />verified</span>
              : <span className="badge warn"><span className="dot" />{b.verify ? "pending" : "never"}</span>}</td>
            <td style={{ textAlign: "right" }}><div className="btnrow" style={{ justifyContent: "flex-end" }}>
              {can("Operator") ? <button className="btn sm" onClick={() => fire(job("POST", `/api/backups/${b.id}/run`))}>Run now</button> : null}
              {can("Admin") && b.workload ? <button className="btn sm" onClick={() => restore(b.workload)}>Restore</button> : null}
              {can("Operator") && b.workload ? <button className="btn sm ghost" onClick={() => fire(job("POST", `/api/backups/${b.id}/verify`))}>Test restore</button> : null}
              {can("Admin") ? <button className="btn sm ghost" onClick={() => fire(act("DELETE", `/api/backups/${b.id}`, undefined, "Schedule removed", b.target))}>Remove</button> : null}
            </div></td>
          </tr>
        )) : <tr><td colSpan={7}><div className="empty"><h3>No backup schedules</h3><p>Add one per workload, or one for every guest on a device.</p></div></td></tr>}
      </tbody></table></div>
    </>
  );
}
PAGES.backups = { crumb: () => "Backups & restore", C: BackupsPage };

/* ---------- recycle bin ---------- */
function RecyclePage() {
  return (
    <>
      <div className="pagehead"><div><h2>Recycle bin</h2><p>Deleted VMs and containers are stopped and kept here for seven days. Disks stay until they are purged.</p></div></div>
      {DB.recycle.length ? (
        <div className="tbl-wrap"><table><thead><tr><th>Name</th><th>Type</th><th>Device</th><th>Deleted</th><th>Purges</th><th>Final backup</th><th></th></tr></thead><tbody>
          {DB.recycle.map((r: any) => (
            <tr key={r.id} className="row"><td><b style={{ fontWeight: 500 }}>{r.name}</b></td>
              <td><span className={`type-badge type-${r.type}`}>{String(r.type || "").toUpperCase()}</span></td>
              <td>{r.node}</td><td>{when(r.deleted)}</td><td className="mono">{inFuture(r.purge)}{r.keepdisk ? " · disks kept" : ""}</td>
              <td>{r.backup ? <span className="badge ok"><span className="dot" />kept</span> : <span className="badge">none</span>}</td>
              <td style={{ textAlign: "right" }}>{can("Admin") ? (
                <div className="btnrow" style={{ justifyContent: "flex-end" }}>
                  <button className="btn sm primary" onClick={() => fire(job("POST", `/api/recycle/${r.id}/restore`))}>Restore</button>
                  <button className="btn sm ghost" onClick={() => Modal.confirm(`Purge ${r.name} now?`,
                    r.keepdisk ? "The VM is destroyed; its disks are moved to the ec-disk-holder VM." : "The guest and its disks are destroyed. Only a backup can bring it back.",
                    "Purge now", () => fire(job("POST", `/api/recycle/${r.id}/purge`)))}>Purge now</button>
                </div>) : null}</td>
            </tr>
          ))}
        </tbody></table></div>
      ) : <div className="empty"><h3>Nothing deleted recently</h3><p>Anything you delete will wait here for seven days before its disks are removed.</p></div>}
    </>
  );
}
PAGES.recycle = { crumb: () => "Recycle bin", C: RecyclePage };
